import { AfterContentInit, Component, OnDestroy, OnInit, ViewChild, } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { animate, state, style, transition, trigger, } from "@angular/animations";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSort, Sort } from "@angular/material/sort";
import { MatTabGroup } from "@angular/material/tabs";
import {
  FilterParameter,
  LoanStatus,
  parseNumber,
  TransactionStatus,
  TransactionType,
  TransactionTypeLabel,
  TransactionTypeString,
  ProductCode,
  QueryParamsService,
  FailureReasonBaseData,
} from "common";
import { TransactionService } from "../transaction.service";
import {
  TransactionListFilterDialogComponent
} from "../transaction-list-filter-dialog/transaction-list-filter-dialog.component";
import { TransactionFilterService } from "../transaction-filter.service";
import { AccountService } from "../../account/account.service";
import { UserPermissionItem, UserPermissionService, } from "../../user/user-permission/user-permission.service";
import { readOnly, readWrite, } from "../../user/user-permission/user-permission.data";
import { routerTransition } from "../../shared/animations/router.animations";
import { AccountData } from "../../account/account.model";
import {
  CanUserEditTransactionInStatus,
  CanUserMoveTransactionToStatus,
  DRAW_REQUIREMENT_GROUP_TITLE,
  DrawRequirementLabel,
  groupDrawRequirementLabels,
  TransactionData,
  TransactionFilters,
  TransactionListComponentData,
  TransactionQueryParams,
  TransactionStatusChips,
} from "../transaction.model";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { AppSettings } from "../../../app.settings";

@UntilDestroy()
@Component({
    selector: "ifb-transaction-list",
    templateUrl: "./transaction-list.component.html",
    styleUrls: ["./transaction-list.component.scss"],
    animations: [
        routerTransition,
        trigger("detailExpand", [
            state("collapsed, void", style({ height: "0px", minHeight: "0", display: "none" })),
            state("expanded", style({ height: "*" })),
            transition("expanded <=> collapsed", animate("225ms cubic-bezier(0.4, 0.0, 0.2, 1)")),
            transition("expanded <=> void", animate("225ms cubic-bezier(0.4, 0.0, 0.2, 1)")),
        ]),
    ],
    standalone: false
})
export class TransactionListComponent
  implements OnInit, OnDestroy, AfterContentInit {
  private _failureReasonsMap: Map<string, string> = null;
  private _failureReasonsOptions: FailureReasonBaseData[] = null;

  filters: TransactionFilters;
  activeFilters: FilterParameter[] = [];
  innerTab: boolean;
  canEditProcessedTransaction = false;
  adminEdit = false;
  data: TransactionListComponentData;
  readWriteTransaction: boolean;
  CanUserEditTransactionInStatusGrantedPermissions: UserPermissionItem[] = [];
  CanUserMoveTransactionToStatusGrantedPermissions: UserPermissionItem[] = [];
  canReadAccounts = false;
  canReadEntities = false;
  canReadTransactions = false;
  animationMode: string;
  expanded: any = {};
  expandedElement: any;
  openedRow: boolean;
  saleforceId: string;
  canMakeDraw: boolean;
  canMakePayment: boolean;
  havePermissionsToSimulate: boolean;
  canSimulateDraw: boolean;
  canSimulatePayment: boolean;
  isOwnerAssigned: boolean;
  loanStatus = LoanStatus;
  selectedTabIndex = 0;
  promotionsToSelectMap: Map<string, string>;
  backToListUrl: string;

  constructor(
    private route: ActivatedRoute,
    private transactionService: TransactionService,
    private dialog: MatDialog,
    private accountService: AccountService,
    private userPermissionService: UserPermissionService,
    private _transactionFilterService: TransactionFilterService,
    private router: Router,
    private queryParamsService: QueryParamsService,
    private settings: AppSettings
  ) { }

  @ViewChild("tabGroup", { static: true }) tabGroup: MatTabGroup;

  readonly TransactionStatus = TransactionStatus;
  readonly status = TransactionStatusChips;
  readonly TransactionTypeString = TransactionTypeString;

  ngOnInit(): void {
      this.route.data.pipe(untilDestroyed(this)).subscribe((it: TransactionListComponentData) => {
        this.data = it;
        this.initFailureReasons(it);
        this.promotionsToSelectMap = new Map(
          it.promotionsToSelect?.map((r): [string, string] => [
            r.id.toString(),
            r.name,
          ])
        );
      });

      this.route.queryParams.pipe(untilDestroyed(this)).subscribe((params) => {
        this.queryParams = new TransactionQueryParams(this.queryParamsService.init((params)));
        this.activeFilters = this._transactionFilterService.addFilterParameters(
          this.queryParams,
          this.promotionsToSelectMap,
          this._failureReasonsMap
        );
        this.backToListUrl = this.router.url;
      });

      this.route.params.pipe(untilDestroyed(this)).subscribe(
        (params) => (this.saleforceId = params.uniqueId)
      );

    this.filters = new TransactionFilters(this.queryParams);
    this.innerTab =
      this.route.snapshot.data.appSidenavItem === "account" ||
      this.data.account != null;
    this.animationMode = this.innerTab ? "fly" : "fade";
    this.isOwnerAssigned =
      this.data.account && this.data.accountInfo
        ? this.data.account.owners && this.data.account.owners.length > 0 : true;

    this.paginator.pageSize = this.queryParams.limit;
    this.paginator.pageIndex = this.queryParams.skip / this.queryParams.limit;

    this.userPermissionService
      .granted([readOnly("servicing-transactions")])
      .subscribe((res) => (this.canReadTransactions = res));

    this.userPermissionService
      .granted([readWrite("servicing-transactions")])
      .subscribe((res) => (this.readWriteTransaction = res));

    this.userPermissionService
      .granted([readWrite("servicing-editing-processed-transactions")])
      .subscribe((res) => (this.canEditProcessedTransaction = res));

    this.userPermissionService
      .granted([readOnly("servicing-simulating-transactions")])
      .subscribe((res: boolean) => {
        this.havePermissionsToSimulate = res;
        this.setSimulationFlags()
      });

    for (
      let index = 0;
      index < CanUserEditTransactionInStatus.length;
      index++
    ) {
      this.CanUserEditTransactionInStatusGrantedPermissions[index] = {
        permission: [CanUserEditTransactionInStatus[index]],
        hidden: true,
      };
    }
    this.userPermissionService
      .visibilityList(this.CanUserEditTransactionInStatusGrantedPermissions)
      .subscribe((res) => res);

    for (
      let index = 0;
      index < CanUserMoveTransactionToStatus.length;
      index++
    ) {
      this.CanUserMoveTransactionToStatusGrantedPermissions[index] = {
        permission: [CanUserMoveTransactionToStatus[index]],
        hidden: true,
      };
    }
    this.userPermissionService
      .visibilityList(this.CanUserMoveTransactionToStatusGrantedPermissions)
      .subscribe((res) => res);

    this.userPermissionService
      .granted([readWrite("servicing-subtransactions")])
      .subscribe((res) => (this.adminEdit = res));

    this.userPermissionService
      .granted([readOnly("servicing-accounts")])
      .subscribe((res) => (this.canReadAccounts = res));

    this.userPermissionService
      .granted([readOnly("servicing-entities")])
      .subscribe((res) => (this.canReadEntities = res));

    this.dataSet();
  }

  ngAfterContentInit(): void {
    this.accountService.obsCurrentLoan
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        if (res) this.data.account = res;
      });
  }

  ngOnDestroy(): void {
    this.accountService.onCurrentLoanChanged.next(null);
  }

  dataSet(): void {
    this.canMakeDraw =
      !this.data.account && !this.data.accountInfo
        ? true
        : this.data.account.allowDraw && !this.data.liquidityEvent?.resolutionStatus;
    this.canMakePayment =
      !this.data.account && !this.data.accountInfo
        ? true
        : this.data.account.allowPayment;
    this.setSimulationFlags();
  }

  activeFilterRemoved(activeFilters: FilterParameter[]): void {
    if (!activeFilters.length) return;
    activeFilters.forEach((item) => {
      this.filters.remove(item.id);
      this._transactionFilterService.filter(
        this.filters,
        this.data,
        this.saleforceId,
        this.paginator,
        this.sort
      );
    });
  }

  downloadHistory(): void {
    const loanId = this.data?.account?.id;
    const query = loanId ? { ...this.queryParams, loanId } : this.queryParams;
    this.transactionService.download(query);
  }

  downloadUpcoming(): void {
    this.accountService.getAmortizationCsv(this.data.account.id);
  }

  downloadFull(): void {
    this.transactionService.downloadHistoryUpcoming(
      this.data.account.id
    );
  }

  showType(type: number): string {
    return TransactionTypeLabel.get(type);
  }

  showReasons(values: string[]): string[] {
    if (!values || values.length === 0 || !this._failureReasonsMap) return;

    return values.map((v) => {
      const name = this._failureReasonsMap.get(v);
      return name ? name : v;
    });
  }

  getBankVerificationAvatarIcon(trx: TransactionData): string {
    if (trx.underwritingDate) {
      if (trx.wasScreenShareRequiredAtUWTime) return "screen_share";
      else if (trx.wasAbleToUnderwriteAtUWTime) return "find_in_page";
      else if (!trx.wasAbleToUnderwriteAtUWTime) return "search_off";
    } else {
      if (trx.isScreenShareRequired) return "screen_share";
      else if (trx.isAbleToUnderwrite) return "find_in_page";
      else if (!trx.isAbleToUnderwrite) return "search_off";
    }
  }

  getBankVerificationAvatarClass(trx: TransactionData): string {
    if (!trx.underwritingDate) {
      if (trx.isScreenShareRequired) return "avatar medium mat-orange-bg";
      else if (trx.isAbleToUnderwrite) return "avatar medium mat-indigo-bg";
      else if (!trx.isAbleToUnderwrite) return "avatar medium mat-red-bg";
    } else {
      return "avatar medium mat-light-grey-bg";
    }
  }

  getBankVerificationTooltip(trx: TransactionData): string {
    if (trx.underwritingDate) {
      if (trx.wasScreenShareRequiredAtUWTime)
        return "Screen share was required at UW time";
      else if (trx.wasAbleToUnderwriteAtUWTime)
        return "Entity was able to underwrite at UW time";
      else if (!trx.wasAbleToUnderwriteAtUWTime)
        return "Entity was not able to underwrite at UW time";
    } else {
      if (trx.isScreenShareRequired) return "Screen share required";
      else if (trx.isAbleToUnderwrite) return "Entity is able to underwrite";
      else if (!trx.isAbleToUnderwrite)
        return "Entity is not able to underwrite";
    }
  }

  getBankLinkingStatusAvatarIcon(trx: TransactionData): string {
    if (trx.underwritingDate) {
      if (trx.wasTransferBankAccountLinkedAtUWTime) return "link";
      else if (trx.wasTransferBankAccountLinkedAtUWTime === false)
        return "link_off";
    } else {
      if (trx.isTransferBankAccountLinked) return "link";
      else if (!trx.isTransferBankAccountLinked) return "link_off";
    }
  }

  getBankLinkingStatusAvatarClass(trx: TransactionData): string {
    if (!trx.underwritingDate) {
      if (trx.isTransferBankAccountLinked) return "avatar medium mat-green-bg";
      else if (!trx.isTransferBankAccountLinked)
        return "avatar medium mat-red-bg";
    } else if (
      trx.wasTransferBankAccountLinkedAtUWTime ||
      trx.wasTransferBankAccountLinkedAtUWTime === false
    ) {
      return "avatar medium mat-light-grey-bg";
    }
  }

  getBankLinkingStatusTooltip(trx: TransactionData): string {
    if (trx.underwritingDate) {
      return trx.wasTransferBankAccountLinkedAtUWTime ?
        "Bank account was linked at UW time" : "Bank account was unlinked at UW time";
    } else {
      return trx.isTransferBankAccountLinked ?
        "Bank account linked" : "Bank account unlinked";
    }
  }

  getConditionsTooltip(trx: TransactionData): string {
    const grouped = groupDrawRequirementLabels(trx?.drawRequirements, {
      requestorState: trx?.customerDrawGeographicalPosition?.region,
      businessState: trx?.businessAddress?.state
    });

    const sections: string[] = [];
    if (grouped.needsAttention.length) {
      sections.push(this.buildTooltipSection(DRAW_REQUIREMENT_GROUP_TITLE.needsAttention, grouped.needsAttention));
    }
    if (grouped.warnings.length) {
      sections.push(this.buildTooltipSection(DRAW_REQUIREMENT_GROUP_TITLE.warnings, grouped.warnings));
    }
    if (grouped.satisfied.length) {
      sections.push(this.buildTooltipSection(DRAW_REQUIREMENT_GROUP_TITLE.satisfied, grouped.satisfied));
    }

    return sections.join('\n\n');
  }

  private buildTooltipSection(header: string, labels: DrawRequirementLabel[]): string {
    return [header, ...labels.map(label => `  • ${label.text}`)].join('\n');
  }

  getConditionsAvatarIcon(trx: TransactionData): string {
    if (trx?.drawRequirements?.some(r => r.required && !r.requirementCompleted)) return "dangerous";
    if (trx?.drawRequirements?.some(r => !r.requirementCompleted && r.advisory)) return "warning";
    return "fact_check";
  }

  getConditionsAvatarClass(trx: TransactionData): string {
    let color: string;

    if (trx.status === TransactionStatus.Pending && trx?.type === TransactionType.Draw) {
      if (trx?.drawRequirements?.some(r => r.required && !r.requirementCompleted))
        color = "mat-red-bg";
      else if (trx?.drawRequirements?.some(r => !r.requirementCompleted && r.advisory))
        color = "mat-orange-bg";
      else
        color = "mat-green-bg";
    } else {
      color = "mat-light-grey-bg";
    }

    return `avatar medium ${color}`;
  }

  getPromotionsString(trx: TransactionData): string {
    return trx.promotions?.map((p) => p.name).join(", ");
  }

  filterDialog(): void {
    TransactionListFilterDialogComponent.show(
      this.dialog,
      this.queryParams,
      this.data,
      this.canReadAccounts,
      this._failureReasonsOptions,
    ).subscribe((result) => {
      if (result) {
        this.filters = result;
        this.paginator.firstPage();
        this._transactionFilterService.filter(
          result,
          this.data,
          this.saleforceId,
          this.paginator,
          this.sort
        );
      }
    });
  }

  sortData(sort: Sort): void {
    this._transactionFilterService.filter(
      this.filters,
      this.data,
      this.saleforceId,
      this.paginator,
      this.sort
    );
  }

  setPage(event: PageEvent): void {
    this._transactionFilterService.filter(
      this.filters,
      this.data,
      this.saleforceId,
      this.paginator,
      this.sort
    );
  }

  fetchData(event): void {
    this.dataSet();

    const uniqueId = this.route.snapshot.params.uniqueId;

    if (uniqueId)
      this.accountService
        .getByUniqueId(uniqueId)
        .subscribe((res: AccountData) => {
          if (res) this.refreshData(parseNumber(res.id));
        });
    else this.refreshData(parseNumber(this.route.snapshot.params.id));
  }

  refreshData(loanId: number): void {
    const query = !loanId
      ? this.queryParams
      : this.data?.account?.renewalFor
        ? { ...this.queryParams, loanId, renewalFor: this.data.account.renewalFor }
        : { ...this.queryParams, loanId };

    this.transactionService
      .query(query)
      .subscribe((res) => (this.data.transactionQueryResult = res));
  }

  @ViewChild("paginator", { static: true })
  paginator: MatPaginator;

  @ViewChild("paginator2", { static: true })
  paginator2: MatPaginator;

  get accountPaymentListPaginator(): MatPaginator {
    if (this.innerTab) return this.paginator2;
    else return null;
  }

  queryParams: TransactionQueryParams;

  @ViewChild(MatSort)
  sort: MatSort;

  getDisplayedColumns(): string[] {
    return this.tableColumns
      .filter((cd) => (this.innerTab ? cd.showInnerTab : cd.showMainList))
      .map((cd) => cd.def);
  }

  tableColumns = [
    { def: "icons", showInnerTab: true, showMainList: true },
    { def: "date", showInnerTab: true, showMainList: true },
    { def: "type", showInnerTab: true, showMainList: true },
    { def: "amount", showInnerTab: true, showMainList: true },
    { def: "status", showInnerTab: true, showMainList: true },
    { def: "nextStatus", showInnerTab: true, showMainList: true },
    { def: "businessName", showInnerTab: false, showMainList: true },
    { def: "facility", showInnerTab: true, showMainList: true },
    { def: "loanNumber", showInnerTab: true, showMainList: true },
    { def: "originalClientId", showInnerTab: true, showMainList: true },
    { def: "rejectReasons", showInnerTab: true, showMainList: true },
    { def: "modifier", showInnerTab: true, showMainList: true },
    { def: "advisor", showInnerTab: false, showMainList: true },
    { def: "salesforce", showInnerTab: true, showMainList: true },
    { def: "bankAccount", showInnerTab: true, showMainList: true },
    { def: "renewalFor", showInnerTab: true, showMainList: false },
    { def: "promotion", showInnerTab: true, showMainList: true },
    { def: "processDate", showInnerTab: true, showMainList: true },
    { def: "disbursedAmount", showInnerTab: true, showMainList: true },
    { def: "thirdPartyPayoffAmount", showInnerTab: true, showMainList: true },
    { def: "deferredFeeAmount", showInnerTab: true, showMainList: true },
    { def: "cashFeeAmount", showInnerTab: true, showMainList: true },
    { def: "principalAmount", showInnerTab: true, showMainList: true },
    { def: "interestAmount", showInnerTab: true, showMainList: true },
    { def: "closingBalance", showInnerTab: true, showMainList: false },
    { def: "outstandingInterest", showInnerTab: true, showMainList: false },
    { def: "outstandingPrincipal", showInnerTab: true, showMainList: false },
    { def: "utilization", showInnerTab: true, showMainList: false },
    { def: "totalDeferredFees", showInnerTab: true, showMainList: false },
    { def: "totalCashFees", showInnerTab: true, showMainList: false },
    { def: "feeType", showInnerTab: true, showMainList: false },
    { def: "brokerId", showInnerTab: true, showMainList: false },
    { def: "brokerName", showInnerTab: true, showMainList: false },
    { def: "totalFees", showInnerTab: true, showMainList: false },
    { def: "totalInterest", showInnerTab: true, showMainList: false },
    { def: "totalPrincipal", showInnerTab: true, showMainList: false },
    { def: "totalPayback", showInnerTab: true, showMainList: false },
    { def: "initiator", showInnerTab: true, showMainList: true },
    { def: "creator", showInnerTab: true, showMainList: true },
    { def: "maintained", showInnerTab: true, showMainList: true },
    { def: "id", showInnerTab: true, showMainList: true },
    { def: "edit", showInnerTab: true, showMainList: true },
  ];

  addTransaction(type: TransactionTypeString): void {
    if (this.data.account && this.data.accountInfo) {
      this.router.navigate([
        `/transaction-new/${type}/${this.data.account.id}`,
      ]);
    } else {
      this.router.navigate([`/transaction-new/${type}`]);
    }
  }

  makeDrawSimulation(): void {
    this.router.navigate([
      `/draw-simulation/${this.data.account.id}`,
    ]);
  }

  makePaymentSimulation(): void {
    this.router.navigate([
      `/payment-simulation/${this.data.account.id}`,
    ]);
  }

  addAdminTransaction() : void {
    if (this.data.account && this.data.accountInfo) {
      this.router.navigate([`/transaction-new-admin/${this.data.account.id}`]);
    } else {
      this.router.navigate([`/transaction-new-admin/`]);
    }
  }

  transactionDetailsLink(tx: TransactionData): void {
    if (!this.canReadTransactions || !this.canReadAccounts) {
      return;
    }

    this.router.navigate([`account/${tx.accountId}/transaction/${tx.id}/view-mode`], { queryParams: { backTo: this.backToListUrl } });
  }

  accountDetailsLink(tx: TransactionData): string {
    if (!this.canReadAccounts || !this.canReadEntities) return;
    return `/account/${tx.accountId}/profile`;
  }

  originalAccountDetailsLink(tx: TransactionData): string {
    if (!this.canReadAccounts || !this.canReadEntities || !tx.originalLoanId) return;
    return `/account/${tx.originalLoanId}/profile`;
  }

  companyDetailsLink(tx: TransactionData): string {
    if (!this.canReadEntities || !tx.entityId) return;
    return `/company/${tx.entityId}/profile`;
  }

  salesforceLink(salesforceRecordId: string): string {
    return this.settings.salesforce.url + salesforceRecordId;
  }

  showDetails(element: any): void {
    this.expandedElement = this.expandedElement !== element ? element : null;
    this.openedRow = this.expandedElement != null;
  }

  private initFailureReasons(it: TransactionListComponentData): void {
    this._failureReasonsMap = new Map(
      it.failureReasons.map((r): [string, string] => [r.code, r.name])
    );
    this._failureReasonsMap.set('EMPTY', "No reason");

    this._failureReasonsOptions = [
      { code: 'EMPTY', name: "No reason" },
      ...it.failureReasons
    ];
  }

  private setSimulationFlags(): void{
    this.canSimulateDraw =
      this.havePermissionsToSimulate && this.data.account && this.data.accountInfo && this.canMakeDraw && this.data.account.productCode !== ProductCode.Term;
    this.canSimulatePayment =
      this.havePermissionsToSimulate && this.data.account && this.data.accountInfo && this.canMakePayment;
  }
}
